import roles from '@/constants/roles';
import { useAppStore } from '@/store/useAppStore';
import { resetUserSession } from '@/services/resetUserSession';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import {
  ACCOUNT_SESSION_MESSAGE_KEY,
  accountOperationInProgress,
  accountStateUnavailable,
  isSessionEndReason,
  maintenanceFreeze,
  notifyAccountState,
  sessionEndMessages,
} from './accountState';
import { createRefreshCoordinator } from './refreshCoordinator';
import { apiPaths } from './paths';
import authMessages from '@/messages/auth';

export type TCustomError = {
  message: string;
  code: number;
  name: string;
  payload: null;
};

export type TCustomAxiosError = {
  code: number;
  data: null;
  success: boolean;
  errors: TCustomError[];
  reason?: string;
  failed?: import('@/types/generated/schemas').InviteUsersFailedRowDto[];
};

export type TCustomAxiosResponse<T> = {
  code: number;
  success: boolean;
  errors: TCustomError[];
  data: T;
  pagination?: {
    page: number;
    limit: number;
    totalPages: number;
    offset: number;
    totalItems: number;
  };
};

// When the dev proxy is on (default), use the relative "/api" so requests flow
// through Vite's proxy. When it's off (VITE_USE_DEV_PROXY=false), talk to the
// API directly via VITE_API_BASE_URL (requires backend CORS).
const useDevProxy = import.meta.env.VITE_USE_DEV_PROXY !== 'false';

const api = axios.create({
  baseURL: import.meta.env.DEV && useDevProxy ? '/api' : import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const refreshApi = axios.create({ baseURL: api.defaults.baseURL, withCredentials: true });
const REFRESH_LOCK = 'omnia-refresh-cookie';
const REFRESH_GENERATION_KEY = 'omnia-refresh-generation';
const UNAVAILABLE_RETRY_DELAYS = [350, 800, 1600];

type RetryableRequest = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _refreshGeneration?: string | null;
  _unavailableRetries?: number;
};

const readRefreshGeneration = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_GENERATION_KEY);
  } catch {
    return null;
  }
};

const markRefreshComplete = () => {
  try {
    localStorage.setItem(REFRESH_GENERATION_KEY, crypto.randomUUID());
  } catch {
    /* Storage may be unavailable. */
  }
};

const refreshOnce = createRefreshCoordinator({
  readGeneration: readRefreshGeneration,
  recordRefresh: markRefreshComplete,
  withLock: async (work) => {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      await navigator.locks.request(REFRESH_LOCK, work);
    } else {
      await work();
    }
  },
  refresh: async () => {
    for (let attempt = 0; ; attempt += 1) {
      try {
        await refreshApi.post('/v1/user/auth/refresh');
        return;
      } catch (error) {
        const response = axios.isAxiosError(error) ? error.response : undefined;
        if (
          response?.status !== 503 ||
          response.data?.reason !== accountStateUnavailable ||
          attempt >= UNAVAILABLE_RETRY_DELAYS.length
        )
          throw error;
        await new Promise((resolve) => setTimeout(resolve, UNAVAILABLE_RETRY_DELAYS[attempt]));
      }
    }
  },
});

let sessionEnding = false;
const endSession = (message: string) => {
  if (sessionEnding) return;
  sessionEnding = true;
  resetUserSession();
  try {
    sessionStorage.setItem(ACCOUNT_SESSION_MESSAGE_KEY, message);
  } catch {
    /* Use sign-in without a message. */
  }
  window.location.replace('/sign-in/');
};

api.interceptors.request.use((config) => {
  const request = config as RetryableRequest;
  request._refreshGeneration ??= readRefreshGeneration();
  return request;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequest | undefined;
    const status = error.response?.status;
    const reason: unknown = error.response?.data?.reason;
    const isPublicRequest = originalRequest?.url?.includes('/v1/public/');
    // Opening an email link must never replace or clear the current account.
    // Let the report page explain link errors without automatic session recovery/logout.
    const isWeeklyReportLink = originalRequest?.url?.startsWith('/v1/peersupport/weekly-report/link/');
    const isInvitationRevokeRequest = /^\/v1\/organization\/[^/]+\/revoke\/[^/]+(?:\/resolve)?$/.test(
      originalRequest?.url ?? '',
    );
    const isPreAuthRequest = ['/login', '/forgot-password', '/set-password', '/verify-token', '/reset-password'].some(
      (path) => originalRequest?.url?.includes(path),
    );
    const isPasswordSubmission =
      originalRequest?.url === apiPaths.user.setInitialPassword || originalRequest?.url === apiPaths.user.resetPassword;

    if (status === 401 && isSessionEndReason(reason)) {
      endSession(sessionEndMessages[reason]);
      return Promise.reject(error.response.data);
    }

    if (status === 503 && reason === accountStateUnavailable && originalRequest) {
      const attempt = originalRequest._unavailableRetries ?? 0;
      if (!isPasswordSubmission && attempt < UNAVAILABLE_RETRY_DELAYS.length) {
        originalRequest._unavailableRetries = attempt + 1;
        await new Promise((resolve) => setTimeout(resolve, UNAVAILABLE_RETRY_DELAYS[attempt]));
        return api(originalRequest);
      }
      if (!isPreAuthRequest) notifyAccountState('Account service is unavailable. Please try again.');
      return Promise.reject({
        ...error.response.data,
        errors: [
          {
            message: 'Account service is unavailable. Please try again.',
            code: 503,
            name: 'ServiceUnavailable',
            payload: null,
          },
        ],
      } satisfies TCustomAxiosError);
    }

    const mayRefresh = !isPublicRequest && !isWeeklyReportLink && !isPreAuthRequest;
    if (status === 401 && originalRequest?._retry && mayRefresh) {
      endSession('Your session expired. Please sign in again.');
    } else if (status === 401 && originalRequest && mayRefresh) {
      originalRequest._retry = true;
      try {
        await refreshOnce(originalRequest._refreshGeneration ?? null);
        originalRequest._refreshGeneration = readRefreshGeneration();
        return api(originalRequest);
      } catch (refreshError) {
        const refreshResponse = axios.isAxiosError(refreshError) ? refreshError.response : undefined;
        const refreshStatus = refreshResponse?.status;
        if (refreshStatus && refreshStatus >= 400 && refreshStatus < 500) {
          const refreshReason: unknown = refreshResponse?.data?.reason;
          if (refreshStatus === 401 && isSessionEndReason(refreshReason)) {
            endSession(sessionEndMessages[refreshReason]);
          } else if (refreshStatus !== 409) {
            endSession('Your session expired. Please sign in again.');
          }
        }
        if (refreshStatus === 503 || refreshStatus === 409) {
          const message =
            refreshStatus === 409 && refreshResponse?.data?.reason === maintenanceFreeze
              ? 'Account changes are paused for planned maintenance.'
              : 'Account service is unavailable. Please try again shortly.';
          notifyAccountState(message);
          return Promise.reject({
            ...(refreshResponse?.data ?? {}),
            code: refreshStatus,
            errors: [{ message, code: refreshStatus, name: 'AccountStateError', payload: null }],
          } satisfies TCustomAxiosError);
        }
        return Promise.reject(refreshError);
      }
    }

    // An upgraded peer-support role may require a refreshed token once.
    if (status === 403 && originalRequest?.url?.includes('/peersupport/profile') && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await refreshOnce(originalRequest._refreshGeneration ?? null);
        originalRequest._refreshGeneration = readRefreshGeneration();
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (!isPublicRequest && !isWeeklyReportLink && !isInvitationRevokeRequest && error.response?.status === 400) {
      const appStore = useAppStore.getState();
      const isSuperAdmin = !!appStore.user?.roles?.includes(roles.superAdmin);
      if (isSuperAdmin) endSession('Please sign in again.');
    }

    if (!error.response) {
      return Promise.reject({
        code: 0,
        data: null,
        success: false,
        errors: [
          {
            message: error.message || 'Network request failed.',
            code: 0,
            name: error.name || 'NetworkError',
            payload: null,
          },
        ],
      } satisfies TCustomAxiosError);
    }

    if (status === 429) {
      if (
        originalRequest?.url === apiPaths.user.verifyAuthToken ||
        originalRequest?.url === apiPaths.user.setInitialPassword
      ) {
        return Promise.reject({ code: 429, data: null, success: false, errors: [] } satisfies TCustomAxiosError);
      }
      const message =
        originalRequest?.url === apiPaths.user.signIn
          ? authMessages.signInRateLimit
          : originalRequest?.url === apiPaths.user.forgotPassword
            ? authMessages.forgotPasswordRateLimit
            : null;

      if (message) {
        return Promise.reject({
          code: 429,
          data: null,
          success: false,
          errors: [{ message, code: 429, name: 'RateLimitError', payload: null }],
        } satisfies TCustomAxiosError);
      }
    }

    const apiError = error.response.data as TCustomAxiosError;

    if (status === 409 && reason === accountOperationInProgress) {
      apiError.errors = [
        {
          message: 'Another account operation is running. Please try again shortly.',
          code: 409,
          name: 'AccountOperationInProgress',
          payload: null,
        },
      ];
      if (!isPreAuthRequest) notifyAccountState(apiError.errors[0].message);
    } else if (status === 409 && reason === maintenanceFreeze) {
      apiError.errors = [
        {
          message: 'Account changes are paused for planned maintenance.',
          code: 409,
          name: 'MaintenanceFreeze',
          payload: null,
        },
      ];
      if (!isPreAuthRequest) notifyAccountState(apiError.errors[0].message);
    } else if (status === 503) {
      apiError.errors = [
        {
          message: 'Service unavailable. Please try again shortly.',
          code: 503,
          name: 'ServiceUnavailable',
          payload: null,
        },
      ];
      if (!isPreAuthRequest) notifyAccountState(apiError.errors[0].message);
    }

    return Promise.reject(apiError);
  },
);

export { api };
